import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';

import Statuses from '../../../../model/Statuses';
import withEnumerableModel from '../../../data/withEnumerableModel';
import propTypes from '../../../propTypes';
import TranslationContext from '../../utils/Translations/TranslationContext';
import SelectBox from '../base/SelectBox';
import MultiselectFilter from './MultiselectFilter';

const StatusSelector = ({
  options,
  optionsByValue,
  customOptions,
  hasMultiStatus,
  hasOmitDeleteStatus,
  onChange,
  value,
  isFilterBar,
  name,
  menuClassName,
  dataCy,
}) => {
  const { t } = useContext(TranslationContext);

  const statusOptions = useMemo(() => {
    let optionsBasic = customOptions ? customOptions.optionsBasic : options;

    optionsBasic =
      optionsBasic && hasOmitDeleteStatus
        ? optionsBasic.filter(({ value }) => Statuses.Deleted.value !== value)
        : optionsBasic;

    if (hasMultiStatus) {
      return optionsBasic.map(option => ({ ...option, id: option.value }));
    }

    return optionsBasic;
  }, [customOptions, options, hasOmitDeleteStatus, hasMultiStatus]);

  const handleStatusChange = useCallback(value => onChange && onChange(value), [
    onChange,
  ]);

  const valueWithFallback = useMemo(() => {
    const _optionsByValue = customOptions
      ? customOptions.optionsByValue
      : optionsByValue;

    let valueWithFallback = _optionsByValue[value];
    if (!value || !valueWithFallback) {
      valueWithFallback =
        _optionsByValue[customOptions ? value : Statuses.Active.value];
    }
    return valueWithFallback.value;
  }, [customOptions, optionsByValue, value]);

  return hasMultiStatus ? (
    <MultiselectFilter
      dataCy={dataCy}
      menuClassName={menuClassName}
      name={name}
      options={statusOptions}
      title={t('Status')}
      value={value}
      onChange={handleStatusChange}
    />
  ) : (
    <SelectBox
      dataCy={dataCy}
      isFilterBar={isFilterBar}
      menuClassName={menuClassName}
      options={statusOptions}
      title={t('Status')}
      value={valueWithFallback}
      onChange={handleStatusChange}
    />
  );
};

StatusSelector.propTypes = {
  ...withEnumerableModel.propTypes,
  value: PropTypes.oneOfType([
    propTypes.status,
    PropTypes.arrayOf(propTypes.status),
  ]),
  onChange: PropTypes.func,
  customOptions: PropTypes.shape({
    optionsByValue: PropTypes.object.isRequired,
    optionsBasic: PropTypes.array.isRequired,
  }),
  hasMultiStatus: PropTypes.bool,
  hasOmitDeleteStatus: PropTypes.bool,
  isFilterBar: PropTypes.bool,
  menuClassName: PropTypes.string,
  dataCy: PropTypes.string,
};

StatusSelector.defaultProps = {
  ...withEnumerableModel.defaultProps,
  value: null,
  onChange: null,
  customOptions: null,
  hasMultiStatus: false,
  hasOmitDeleteStatus: false,
  isFilterBar: true,
};

export default withEnumerableModel(Statuses)(StatusSelector);
